/**
 * AlertsWatcher — фонове опитування проблем + нових здач в ОТК (Sprint F3).
 *
 * Для наглядових ролей (master/admin/menedzher/otk): кожні ~20с тягне нові
 * «Виклики майстра» з /api/v1/alerts. На нову проблему:
 *   - звуковий сигнал (WebAudio),
 *   - тост,
 *   - браузерний push (Notification API) — працює і коли вкладка у фоні.
 *
 * Додатково для ОТК/майстра/адміна: опитує чергу /api/v1/qc/sessions і на
 * НОВУ здачу швеї — той самий сигнал+тост+push (щоб не треба було сидіти в
 * черзі й дивитись). Здача більше не «згорає» — це просто черга.
 *
 * Монтується один раз у корені App; сам вирішує, працювати чи ні (за роллю).
 * lastTs тримаємо в localStorage — щоб не алертити старі події після reload.
 */
import { useEffect, useRef } from "react";
import { apiGet } from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { toast } from "@/store/toast";

// 60 с, як у Kanban і Dashboard. Було 20 с — це найчастіший фоновий запит у
// системі, і він іде з кожної вкладки керівних ролей одночасно. Проблема з
// цеху не втрачає актуальності за 40 секунд, а сервер — домашній ПК.
const POLL_MS = 60_000;
const LS_KEY = "mes-alerts-lastts";
const VIEW_ROLES = ["master", "admin", "menedzher", "otk"];
// Хто чує сигнал на нову здачу (ті, хто фізично приймає).
const QC_ALERT_ROLES = ["otk", "master", "admin"];
// Хто чує сигнал на нове готове-до-відвантаження замовлення.
const SHIP_ALERT_ROLES = ["vidgruzka", "master", "admin"];

interface AlertItem {
  id: number;
  ts: string | null;
  stage: string;
  order_id: number | null;
  cutting_card: string | null;
  project_name: string;
  reason: string;
  who: string;
}

function beep() {
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    [880, 660, 990].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      g.gain.setValueAtTime(0, now + i * 0.18);
      g.gain.linearRampToValueAtTime(0.3, now + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.16);
      osc.connect(g).connect(ctx.destination);
      osc.start(now + i * 0.18);
      osc.stop(now + i * 0.18 + 0.18);
    });
    setTimeout(() => ctx.close().catch(() => undefined), 1200);
  } catch {
    /* без звуку не критично */
  }
}

export function AlertsWatcher() {
  const claims = useAuthStore((s) => s.claims);
  const roles = claims?.roles ?? [];
  const active = roles.some((r) => VIEW_ROLES.includes(r));
  const shipActive = roles.some((r) => SHIP_ALERT_ROLES.includes(r));
  const lastTsRef = useRef<string>(localStorage.getItem(LS_KEY) || "");

  useEffect(() => {
    if (!active && !shipActive) return;

    // Просимо дозвіл на push (одноразово).
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => undefined);
    }

    let stop = false;
    let timer: number | undefined;

    function notify(items: AlertItem[]) {
      beep();
      const first = items[0];
      const label = first.cutting_card
        ? `${first.stage}: ${first.cutting_card}${first.project_name ? " · " + first.project_name : ""}`
        : `${first.stage}: проблема`;
      const body =
        (first.reason ? first.reason : "Виклик майстра") +
        (items.length > 1 ? ` (+${items.length - 1} ще)` : "");
      toast.warning(`🆘 ${label} — ${body}`);
      try {
        if ("Notification" in window && Notification.permission === "granted") {
          const n = new Notification(`🆘 Проблема · ${label}`, {
            body,
            tag: "mes-problem",
          });
          n.onclick = () => {
            window.focus();
            window.location.href = "/problems";
          };
        }
      } catch {
        /* push не критичний */
      }
    }

    async function tick() {
      try {
        const since = lastTsRef.current;
        const q = since ? `?since=${encodeURIComponent(since)}` : "";
        const d = await apiGet<{ items: AlertItem[]; server_now: string }>(
          `/api/v1/alerts${q}`,
        );
        // Перший прохід (без since) — лише встановлюємо межу, не алертимо старе.
        if (since && d.items.length > 0) notify(d.items);
        lastTsRef.current = d.server_now;
        localStorage.setItem(LS_KEY, d.server_now);
      } catch {
        /* тихо — не спамимо тостами при мережевих збоях */
      }
      if (!stop) timer = window.setTimeout(tick, POLL_MS);
    }

    // /alerts доступний лише наглядовим ролям (VIEW_ROLES); vidgruzka туди 403.
    if (active) tick();

    // --- Друга черга: нові здачі в ОТК (звук + тост + push) ---
    const qcActive = roles.some((r) => QC_ALERT_ROLES.includes(r));
    let qcSeen: Set<string> | null = null; // null = ще не робили першого поллу
    let qcTimer: number | undefined;

    async function qcTick() {
      try {
        const d = await apiGet<{
          items: { token: string; seamstress_name: string }[];
        }>("/api/v1/qc/sessions");
        if (qcSeen === null) {
          // Перший прохід — лише запамʼятовуємо наявні, не сигналимо старе.
          qcSeen = new Set(d.items.map((i) => i.token));
        } else {
          const fresh = d.items.filter((i) => !qcSeen!.has(i.token));
          if (fresh.length > 0) {
            beep();
            const first = fresh[0];
            const extra = fresh.length > 1 ? ` (+${fresh.length - 1} ще)` : "";
            toast.success(`🧵 Нова здача в ОТК: ${first.seamstress_name}${extra}`);
            try {
              if ("Notification" in window && Notification.permission === "granted") {
                const n = new Notification(`🧵 Нова здача · ${first.seamstress_name}`, {
                  body: "Здано пачки в ОТК — можна приймати",
                  tag: "mes-qc-submit",
                });
                n.onclick = () => {
                  window.focus();
                  window.location.href = "/qc";
                };
              }
            } catch {
              /* push не критичний */
            }
          }
          qcSeen = new Set(d.items.map((i) => i.token));
        }
      } catch {
        /* тихо */
      }
      if (!stop) qcTimer = window.setTimeout(qcTick, POLL_MS);
    }

    if (qcActive) qcTick();

    // --- Третя черга: нові готові до відвантаження (звук + тост + push) ---
    let shipSeen: Set<number> | null = null; // null = ще не робили першого поллу
    let shipTimer: number | undefined;

    async function shipTick() {
      try {
        const d = await apiGet<{
          ready: { id: number; cutting_card: string; project_name: string }[];
        }>("/api/v1/shipping/orders");
        if (shipSeen === null) {
          // Перший прохід — лише запам'ятовуємо наявні, не сигналимо старе.
          shipSeen = new Set(d.ready.map((o) => o.id));
        } else {
          const fresh = d.ready.filter((o) => !shipSeen!.has(o.id));
          if (fresh.length > 0) {
            beep();
            const first = fresh[0];
            const extra = fresh.length > 1 ? ` (+${fresh.length - 1} ще)` : "";
            const label = `${first.cutting_card}${first.project_name ? " · " + first.project_name : ""}`;
            toast.success(`🚚 Готове до відвантаження: ${label}${extra}`);
            try {
              if ("Notification" in window && Notification.permission === "granted") {
                const n = new Notification(`🚚 На відвантаження · ${first.cutting_card}`, {
                  body: "Замовлення спаковано — можна відвантажувати",
                  tag: "mes-shipping",
                });
                n.onclick = () => {
                  window.focus();
                  window.location.href = "/shipping";
                };
              }
            } catch {
              /* push не критичний */
            }
          }
          shipSeen = new Set(d.ready.map((o) => o.id));
        }
      } catch {
        /* тихо */
      }
      if (!stop) shipTimer = window.setTimeout(shipTick, POLL_MS);
    }

    if (shipActive) shipTick();

    return () => {
      stop = true;
      if (timer) window.clearTimeout(timer);
      if (qcTimer) window.clearTimeout(qcTimer);
      if (shipTimer) window.clearTimeout(shipTimer);
    };
    // roles-залежність навмисно опущена: масив нестабільний (новий реф щоразу)
    // і перезапускав би поллери, скидаючи *Seen. Гейт за active/shipActive достатній.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, shipActive]);

  return null;
}
